import { useMemo } from 'react'
import GcpRuleBasedCallout from '@/components/gcp/GcpRuleBasedCallout'
import GcpSectionErrorBanner from '@/components/gcp/GcpSectionErrorBanner'
import GcpStatCard from '@/components/gcp/GcpStatCard'
import {
  asNumber,
  asRecordArray,
  asString,
  formatUsd,
  getSectionData,
  type RuleBasedRecommendation,
} from '@/components/gcp/gcpHelpers'
import type {
  ClientPath,
  ConnectionPath,
  EgressDailyPoint,
  GcpAnalysisResult,
  SqlInstanceEgress,
} from '@/services/gcpCosts'
import { isGcpSectionError } from '@/services/gcpCosts'

interface GcpEgressSectionProps {
  analysis: GcpAnalysisResult
}

function pathBadgeClass(pathLabel: string): string {
  if (pathLabel === 'public_ip_only') {
    return 'bg-red-100 text-red-800'
  }
  if (pathLabel === 'private_ip') {
    return 'bg-green-100 text-green-800'
  }
  return 'bg-gray-100 text-gray-700'
}

function pathBadgeLabel(pathLabel: string): string {
  if (pathLabel === 'public_ip_only') return 'Public IP only'
  if (pathLabel === 'private_ip') return 'Private IP'
  return 'Unknown'
}

function clientPathChipClass(verdict: ClientPath['path_verdict']): string {
  if (verdict === 'socket_public' || verdict === 'tcp_public') {
    return 'bg-red-100 text-red-800'
  }
  if (verdict === 'tcp_private') {
    return 'bg-green-100 text-green-800'
  }
  return 'bg-gray-100 text-gray-700'
}

function DailySentBars({ daily, instance }: { daily: EgressDailyPoint[]; instance: string }) {
  const chartData = useMemo(
    () => [...daily].sort((a, b) => a.date.localeCompare(b.date)).slice(-30),
    [daily]
  )

  if (chartData.length === 0) {
    return <p className="text-sm text-gray-500">No sent-bytes metrics for {instance}.</p>
  }

  const maxGb = Math.max(...chartData.map((d) => d.sent_gb), 0.0001)

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-gray-700">{instance}</p>
      <div className="flex items-end gap-px h-24 rounded border border-gray-100 bg-gray-50 px-2 py-2">
        {chartData.map((point) => {
          const heightPct = Math.max((point.sent_gb / maxGb) * 100, point.sent_gb > 0 ? 4 : 0)
          return (
            <div
              key={point.date}
              className="group relative flex-1 min-w-[3px] max-w-[12px]"
              title={`${point.date}: ${point.sent_gb.toFixed(2)} GB`}
            >
              <div
                className="w-full rounded-t bg-sky-500"
                style={{ height: `${heightPct}%` }}
              />
            </div>
          )
        })}
      </div>
      <div className="flex justify-between text-[11px] text-gray-500">
        <span>{chartData[0]?.date.slice(5)}</span>
        <span>{chartData[chartData.length - 1]?.date.slice(5)}</span>
      </div>
    </div>
  )
}

export default function GcpEgressSection({ analysis }: GcpEgressSectionProps) {
  const section = analysis.sections.egress
  const data = getSectionData(analysis, 'egress')

  const recommendations = useMemo(
    () =>
      data
        ? (asRecordArray(data.recommendations) as RuleBasedRecommendation[])
        : [],
    [data]
  )

  const sqlInstanceEgress = useMemo(
    () =>
      (data ? asRecordArray(data.sql_instance_egress) : []).map((row) => ({
        instance: asString(row.instance),
        daily: asRecordArray(row.daily).map((d) => ({
          date: asString(d.date),
          sent_gb: asNumber(d.sent_gb) ?? 0,
        })),
        total_gb: asNumber(row.total_gb) ?? 0,
        est_monthly_cost: asNumber(row.est_monthly_cost) ?? 0,
        window_days: asNumber(row.window_days) ?? analysis._meta.metrics_days,
      })) as SqlInstanceEgress[],
    [analysis._meta.metrics_days, data]
  )

  const connectionPaths = useMemo(
    () =>
      (data ? asRecordArray(data.connection_paths) : []).map((row) => ({
        instance: asString(row.instance),
        ipv4_enabled: Boolean(row.ipv4_enabled),
        private_network:
          row.private_network == null ? null : asString(row.private_network),
        authorized_networks_count: asNumber(row.authorized_networks_count) ?? 0,
        path_label: asString(row.path_label) as ConnectionPath['path_label'],
      })) as ConnectionPath[],
    [data]
  )

  const clientPaths = useMemo(
    () =>
      (data ? asRecordArray(data.client_paths) : []).map((row) => ({
        client: asString(row.client),
        client_kind: asString(row.client_kind) || 'service',
        instance: row.instance == null ? null : asString(row.instance),
        path_verdict: asString(row.path_verdict) as ClientPath['path_verdict'],
        path_label: asString(row.path_label),
        evidence: asString(row.evidence),
        billed:
          row.billed === null || row.billed === undefined
            ? null
            : Boolean(row.billed),
      })) as ClientPath[],
    [data]
  )

  if (isGcpSectionError(section)) {
    return (
      <GcpSectionErrorBanner
        title="Network egress analysis failed"
        error={section.error}
        missingRoleHint={section.missing_role_hint}
      />
    )
  }

  if (!data) return null

  const headline = asNumber(data.headline_run_rate_usd_monthly) ?? 0
  const costSkuRows = asRecordArray(data.costs_by_service_sku)

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500">
        Cross-service egress billing, Cloud SQL sent-bytes metrics, and connection-path
        checks for public-IP data paths.
      </p>

      <GcpStatCard
        label="Network egress run rate"
        value={`${formatUsd(headline)}/mo`}
        subtext="Egress SKU billing extrapolated to a monthly run rate"
      />

      {sqlInstanceEgress.length > 0 ? (
        <div className="space-y-4">
          <h3 className="container-title">Cloud SQL sent bytes (daily)</h3>
          <div className="grid gap-6 md:grid-cols-2">
            {sqlInstanceEgress.map((inst) => (
              <DailySentBars key={inst.instance} daily={inst.daily} instance={inst.instance} />
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Instance</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Window GB</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Est. monthly</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sqlInstanceEgress.map((inst) => (
                  <tr key={inst.instance}>
                    <td className="px-3 py-2 text-gray-800">{inst.instance}</td>
                    <td className="px-3 py-2 text-right text-gray-800">
                      {inst.total_gb.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-800">
                      {formatUsd(inst.est_monthly_cost)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {clientPaths.length > 0 ? (
        <div className="space-y-2">
          <h3 className="container-title">
            Who connects to your databases, and how
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Client</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Kind</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Instance</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Path</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Evidence</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {clientPaths.map((path) => (
                  <tr key={`${path.client}-${path.client_kind}`}>
                    <td className="px-3 py-2 text-gray-800">{path.client}</td>
                    <td className="px-3 py-2 text-gray-600">{path.client_kind}</td>
                    <td className="px-3 py-2 text-gray-800">{path.instance ?? '—'}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${clientPathChipClass(path.path_verdict)}`}
                      >
                        {path.path_label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-600 max-w-md truncate">
                      {path.evidence}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {connectionPaths.length > 0 ? (
        <div className="space-y-2">
          <h3 className="container-title">Connection paths</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Instance</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Path</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">IPv4</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Private network</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">
                    Authorized nets
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {connectionPaths.map((path) => (
                  <tr key={path.instance}>
                    <td className="px-3 py-2 text-gray-800">{path.instance}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${pathBadgeClass(path.path_label)}`}
                      >
                        {pathBadgeLabel(path.path_label)}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-800">
                      {path.ipv4_enabled ? 'Yes' : 'No'}
                    </td>
                    <td className="px-3 py-2 text-gray-600 max-w-xs truncate">
                      {path.private_network ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-800">
                      {path.authorized_networks_count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {costSkuRows.length > 0 ? (
        <div className="space-y-2">
          <h3 className="container-title">Egress billing by service & SKU</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Month</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Service</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">SKU</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Net cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {costSkuRows.slice(0, 25).map((row, index) => (
                  <tr key={index}>
                    <td className="px-3 py-2 text-gray-800">{asString(row.month)}</td>
                    <td className="px-3 py-2 text-gray-800">{asString(row.service)}</td>
                    <td className="px-3 py-2 text-gray-600 max-w-md truncate">
                      {asString(row.sku)}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-800">
                      {formatUsd(asNumber(row.net_cost))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {recommendations.length > 0 ? (
        <div className="space-y-3">
          <h3 className="container-title">Recommendations</h3>
          {recommendations.map((rec, index) => (
            <GcpRuleBasedCallout key={`${rec.instance ?? rec.type ?? index}`} recommendation={rec} />
          ))}
        </div>
      ) : null}
    </div>
  )
}
